import { t } from "@travily/i18n";
import { productPath, servicePath } from "@travily/routes";
import type ListingsCard from "@travily/types/models/ListingsCard";
import type {
  ListingsCardFavoriteTrait,
  ListingsCardPhotosTrait,
  ListingsCardPhotoTrait,
  ListingsCardPricingTrait,
  ListingsCardReviewsTrait,
  ListingsCardSellerTrait,
} from "@travily/types/models/ListingsCard";
import { formatPrice } from "@ui/lib/format-price";
import type { CardTheme } from "@ui/types/card";
import { stripHtml } from "@ui/utils/strip-html";
import { ListingCard } from "@/components/ListingCard/ListingCard";
import {
  resolveListingAuthor,
  resolveListingImage,
  resolveListingImages,
} from "@/components/ListingCard/resolve-listing";
import { resolveApproximatePrice } from "@/components/PriceBreakdown/PriceBreakdown";

export type ListingCardListing = ListingsCard &
  (ListingsCardPhotosTrait | ListingsCardPhotoTrait) &
  Partial<ListingsCardPricingTrait & ListingsCardReviewsTrait & ListingsCardSellerTrait & ListingsCardFavoriteTrait>;

function formattedPrice(listing: ListingCardListing): string | null {
  if (!("price_cents" in listing)) return null;
  if (listing.free) return t("components.views.shared.listing_card.free");
  if (listing.price_cents != null && listing.price_currency != null) {
    return formatPrice(listing.price_cents, listing.price_currency);
  }
  return null;
}

export interface ListingCardAdapterProps {
  className?: string;
  favorited?: boolean;
  listing: ListingCardListing;
  onFavoriteToggle?: () => void;
  showAuthor?: boolean;
  showDescription?: boolean;
  showPrice?: boolean;
  showRating?: boolean;
  theme?: CardTheme;
}

/** Maps a listing (Product or Service) onto the presentational ListingCard. */
export function ListingCardAdapter({
  className,
  favorited,
  listing,
  onFavoriteToggle,
  showAuthor,
  showDescription = true,
  showPrice = true,
  showRating = true,
  theme,
}: ListingCardAdapterProps) {
  const image = resolveListingImage(listing);
  const images = resolveListingImages(listing);

  const price = showPrice ? formattedPrice(listing) : null;
  const { approximate, priceHoverContent } = showPrice
    ? resolveApproximatePrice(listing)
    : { approximate: false, priceHoverContent: undefined };
  const reviewsCount = "reviews_count" in listing ? listing.reviews_count : undefined;
  const averageScore = "average_score" in listing ? listing.average_score : undefined;
  const rating =
    showRating && averageScore != null && reviewsCount != null && reviewsCount > 0
      ? { score: Number(averageScore), count: reviewsCount }
      : undefined;
  const author = showAuthor ? resolveListingAuthor(listing) : undefined;

  return (
    <ListingCard
      author={author}
      className={className}
      description={
        showDescription ? (listing.tagline ?? (listing.description ? stripHtml(listing.description) : null)) : null
      }
      favorited={favorited}
      href={listing.listing_type === "Service" ? servicePath(listing.sqid) : productPath(listing.sqid)}
      image={image}
      images={images.length > 1 ? images : undefined}
      linkLabel={listing.title ?? ""}
      onFavoriteToggle={onFavoriteToggle}
      price={price}
      priceApproximate={approximate}
      priceHoverContent={priceHoverContent}
      rating={rating}
      theme={theme}
      title={listing.title ?? ""}
    />
  );
}
